import { TableCell } from '@material-ui/core';
import React, { useState } from 'react';
import { DomainGq } from '../../../../../../graphql/projects/models/DomainGq';
import { ProjectKeywordGq } from '../../../../../../graphql/projects/models/ProjectKeywordGq';
import TimelineChart, { TimelineChartDataPoint } from '../../../../../voxel/Charts/TimelineChart';
import VoxelDataTableCell from '../../../../../voxel/DataTable/VoxelDataTableCell';
import VoxelDialog from '../../../../../voxel/Dialogs/VoxelDialog';
import VoxelDialogContent from '../../../../../voxel/Dialogs/VoxelDialogContent';
import RankSmallSvg from './RankSmallSvg';
import { TrackingData } from './TableCellDomain';
import { getRankColor } from './rankColorScheme';

export interface TableCellTrackingRankProps {
  trackingValues: TrackingData[];
  firstTrackingValue?: TrackingData;
  lastTrackingValue?: TrackingData;
  rankColor: boolean;
  keyword: ProjectKeywordGq;
  domain: DomainGq;
  borderLeft?: boolean;
}

const TableCellTrackingRank: React.FC<TableCellTrackingRankProps> = ({
  trackingValues,
  rankColor,
  keyword,
  domain,
  firstTrackingValue,
  lastTrackingValue,
  borderLeft,
}) => {
  // Chart ...
  const width = Math.max(Math.min(1000, window.document.body.clientWidth * 0.6), 400);
  const largeFontSize = 13;
  const paddingWidth = 3.325 * 4;
  const bigChartProps = {
    paddingTop: largeFontSize * 1,
    width: width - paddingWidth * 2,
    paddingBottom: largeFontSize * 2,
    paddingLeft: largeFontSize * 5,
    paddingRight: largeFontSize * 4,
    fontSizeAxes: largeFontSize,
    fontSizeTooltip: 14,
    showXGrid: true,
    showYGrid: true,
    yTickCount: 5,
    height: 100 * 3.5,
    strokeWidth: 3,
    enableTooltips: true,
  };

  // Dialogs
  const [rankDialogOpen, setRankDialogOpen] = useState<boolean>();

  if (!keyword.serpResultTime || !lastTrackingValue)
    return <VoxelDataTableCell className={`${borderLeft && 'border-l'}`} />;

  return (
    <VoxelDataTableCell
      align={'right'}
      type='number'
      className={`${borderLeft && 'border-l'}
        ${lastTrackingValue && rankColor ? getRankColor(lastTrackingValue?.trackingRank?.rank) : ''}
      `}
    >
      {/* Cell content */}
      <div className={`flex flex-row justify-end items-center gap-3 flex-nowrap`}>
        <span className={`font-medium ${rankColor ? 'text-white' : ' text-voxel-gray-700'}`}>
          {lastTrackingValue.trackingRank?.rank ?? '100+'}
        </span>
        <div
          className={`rounded-sm 
          outline outline-1 outline-offset-2 outline-voxel-gray-200 
          ${rankColor ? 'hover:outline-white/75' : 'hover:outline-voxel-blue-400'
            }`}
          onClick={() => setRankDialogOpen(true)}
          onMouseDown={(event: React.MouseEvent<any>) => {
            event.stopPropagation();
          }}
        >
          <RankSmallSvg trackingValues={trackingValues} rankColor={rankColor} />
        </div>
      </div>

      {/* Rank dialog */}
      {rankDialogOpen && (
        <VoxelDialog
          open={rankDialogOpen}
          onClose={() => setRankDialogOpen(false)}
          maxWClass="max-w-6xl"
          closeOnBackdrop
          title={`${keyword.keyword}`}
          subtitle={`Weekly rank on '${keyword.keyword}'`}
          onMouseDown={(event: React.MouseEvent<any>) => {
            event.stopPropagation();
          }}
        >
          <VoxelDialogContent>
            <div className="flex flex-row justify-center items-center -mt-3">
              <TimelineChart
                {...bigChartProps}
                dataSeries={[
                  trackingValues
                    .filter((tv) => !!tv.trackingRequest)
                    .map(
                      (tv): TimelineChartDataPoint => ({
                        date: tv.scheduledDate.toDate(),
                        value: tv.trackingRank?.rank ?? 100,
                      })
                    ),
                ]}
                disableArea
                reverseY
                yLabelTooltip="Rank"
                yMinPlaceholder={1}
                yMaxPlaceholder={100}
              />
            </div>
          </VoxelDialogContent>
        </VoxelDialog>
      )}
    </VoxelDataTableCell>
  );
};

export default TableCellTrackingRank;
